import * as React from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {InputEvent, PlanInterface, SelectValueProps} from '../types';
import {MutationPlan} from '../../services/plan/MutationPlan';
import { planFormSchema, PlanFormValues } from '../components/plans/PlanFormInfo.type';
import { usePlanFormInfo } from '../components/plans/PlanFormInfo.use-case';

const BILLING_TRIGGER = [
  {
    label: 'Ao iniciar o plano (pré-pago)',
    value: 'inicio_do_periodo'
  },
  {
    label: 'Após o período (pós-pago)',
    value: 'fim_do_periodo'
  },
  {
    label: 'Dia fixo do mês',
    value: 'dia_do_mes'
  },
];

interface ExtendedPlanInterface extends PlanInterface {
  color?: string;
  coverUrl?: string;
}

interface ProgramsContextProps {
  plan: ExtendedPlanInterface;
  setPlan: InputEvent;
  programs: any[];
  externalStoreEnabled: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (data: PlanFormValues) => void;
  externalPlansOptions: SelectValueProps[];
  programsOptions: SelectValueProps[];
  loading: boolean;
  setLoading?: InputEvent;
  errors: any;
  setErrors: InputEvent;
  setUploadImage: (value: any) => void;
}

interface PlanContextProviderProps {
  children: React.ReactNode | React.ReactNode[];
  plan: any;
  programs: any[];
  externalStoreEnabled: boolean;
  externalStoreOptions: [string];
}

export const PlanContext = React.createContext({} as ProgramsContextProps);

export function PlanContextProvider({ children, plan: planProps, programs, externalStoreEnabled, externalStoreOptions,} : PlanContextProviderProps){
  const [plan, setPlan] = React.useState<ExtendedPlanInterface>(planProps || {} as ExtendedPlanInterface);
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState({});
  const {setValue} = useFormContext<PlanFormValues>();
  
  
  const programsOptions = React.useMemo<SelectValueProps[]>(() => {
    return programs.map( p => ({value: `${p.id}`, label: `${p.name} (${p.filial.name})`}))
  }, [programs])

  const handleChange = ({ target }: { target: any }) => {
    const value = target.type == 'tel' ? (target.value.match(/[\d\.|,]+/) || [])[0] || '' : target.value;
    const newPlan = {...plan, [target.name]: value};
    setPlan(newPlan);
    
    setValue(target.name as keyof PlanFormValues, value);
  }

  const setUploadImage = (file: any) => {
    setPlan({...plan, cover: file, coverUrl: file.preview || file.path || ''});
    
    setValue('coverUrl', file.preview || file.path || '');
  };

  const onSubmit = (data: PlanFormValues) => {
    if(loading) return false;

    setLoading(true);

    const planPrograms = data.planPrograms || [];
    const externalPlans = data.externalPlans || [];

    const excludedPlanPrograms = (plan.planPrograms || []).filter( pp => planPrograms.map((p: any) => p.value).indexOf(`${pp.programId}`) === -1 );
    const selectedPlanPrograms = (plan.planPrograms || []).filter(ep => planPrograms.map((op: any) => op.value).indexOf(`${ep.programId}`) > -1);
    const newPlanPrograms = planPrograms.filter( (ep: any) => (plan.planPrograms || []).map(p => `${p.programId}`).indexOf(`${ep.value}`) === - 1 );

    const planProgramsFiltered = [
      ...selectedPlanPrograms.map( ep => ({id: `${ep.id}`, programId: `${ep.programId}`})), 
      ...excludedPlanPrograms.map(ep => ({id: `${ep.id}`, _destroy: true})),
      ...newPlanPrograms.map(pp => ({id: '', programId: `${pp.value}`}))
    ];

    const excludedExternalPlans = (plan.externalPlans || []).filter(ep => externalPlans.map((op: any) => op.value).indexOf(ep.store) === -1);
    const selectedExternalPlans = (plan.externalPlans || []).filter(ep => externalPlans.map((op: any) => op.value).indexOf(ep.store) > -1);
    const newExternalPlans = externalPlans.filter( (ep: any) => (plan.externalPlans || []).map(p => p.store).indexOf(`${ep.value}`) === - 1 );
    const externalPlansFiltered = [
      ...selectedExternalPlans.map( ep => ({id: `${ep.id}`, store: ep.store})), 
      ...excludedExternalPlans.map(ep => ({id: `${ep.id}`, store: ep.store, _destroy: true})), 
      ...newExternalPlans.map(ep => ({id: '', store: ep.value})) 
    ];

    const cycle = typeof plan?.cycles === 'number' && plan.cycles > 0 ? plan.cycles : '0';
    const billingTriggerDay = plan?.billingTriggerDay ? plan.billingTriggerDay : '0';
    const recurrenceValue = typeof plan?.recurrenceValue !== 'number' || plan.recurrenceValue <= 0 ? 1 : plan.recurrenceValue;
    const installments = typeof plan?.installments !== 'number' || plan.installments <= 0 ? 1 : plan.installments;
    
    const confirmed = planProgramsFiltered.length ? true : confirm('Seu plano não dá acesso a nenhum programa de treino. Quer salvar mesmo assim?')
    if(confirmed){
      const newPlan = {
        ...plan, 
        ...data,
        planPrograms: planProgramsFiltered, 
        externalPlans: externalPlansFiltered, 
        cycle, 
        billingTriggerDay, 
        recurrenceValue, 
        installments
      };
      
      MutationPlan(newPlan, setLoading)
      .then(({plan: planUpdated}) => {
        if(!plan.id) {
          const accountName = (window as any).account || '';
          window.history.replaceState("object or string", "", `/${accountName}/admin/plans/${planUpdated.id}/edit`);
        }
        setPlan(planUpdated);
      })
      .catch(err => {
        setErrors(err);
        console.log('MutationPlan ', err)
      })
    } else {
      setLoading(false);
    }
  }

  const externalPlansOptions = [
    {label: 'mundipagg', value: 'mundipagg'},
    {label: 'vindi', value: 'vindi'},
    {label: 'stripe', value: 'stripe'},
  ].filter( e => externalStoreOptions.indexOf(e.value) > -1)

  React.useEffect(() => {
    if (!plan.billingTriggerType) {
      setValue('billingTriggerType', BILLING_TRIGGER[0].value);
    }
  },[]);
  
  return(
    <PlanContext.Provider
      value={{
        plan,
        setPlan,
        programs,
        programsOptions,
        externalStoreEnabled,
        handleChange,
        externalPlansOptions,
        loading,
        setLoading,
        errors,
        setErrors,
        onSubmit,
        setUploadImage,
      }}
    >
      {children}
    </PlanContext.Provider>
  )
}

export const usePlanContext = () => {
  return React.useContext(PlanContext);
}